import { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  usePaymentMethodFlags,
  useUpdatePaymentMethods,
} from '@/hooks/useSettings';
import type {
  PaymentMethodFlags,
  PaymentMethodKey,
} from '@/apis/settings';

const METHOD_LABELS: Record<PaymentMethodKey, string> = {
  stripe: 'Credit Card (Stripe)',
  paypal: 'PayPal',
  npa: 'Credit Card (NPA)',
  send_request: 'Save my order for later',
  check: 'Check',
  bank_transfer: 'Bank Transfer',
  zelle: 'Zelle',
  ach: 'ACH Transfer',
};

const METHOD_ORDER: PaymentMethodKey[] = [
  'stripe',
  'paypal',
  'npa',
  'send_request',
  'check',
  'bank_transfer',
  'zelle',
  'ach',
];

export function SettingsPage() {
  const { data, isLoading } = usePaymentMethodFlags();
  const updateMutation = useUpdatePaymentMethods();
  const [draft, setDraft] = useState<PaymentMethodFlags | null>(null);

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const toggle = (key: PaymentMethodKey) => {
    setDraft((prev) => (prev ? { ...prev, [key]: !prev[key] } : prev));
  };

  const handleSave = () => {
    if (draft) updateMutation.mutate(draft);
  };

  const isDirty =
    !!draft &&
    !!data &&
    METHOD_ORDER.some((k) => draft[k] !== data[k]);

  return (
    <div className="p-6">
      <h1 className="text-black mb-6 text-2xl font-bold">Settings</h1>

      <div className="border-smoke rounded-lg border bg-neutral-white p-6">
        <h2 className="text-black mb-1 text-lg font-semibold">
          Payment Methods
        </h2>
        <p className="text-lightGray mb-6 text-xs">
          Toggle which payment methods are available at storefront checkout.
        </p>

        {isLoading || !draft ? (
          <p className="text-lightGray text-sm">Loading…</p>
        ) : (
          <div className="flex flex-col gap-4">
            {METHOD_ORDER.map((key) => (
              <div
                key={key}
                className="flex items-center justify-between border-smoke border-b pb-4 last:border-0 last:pb-0"
              >
                <span className="text-black text-base font-normal">
                  {METHOD_LABELS[key]}
                </span>
                <Switch
                  checked={draft[key]}
                  onChange={() => toggle(key)}
                />
              </div>
            ))}

            <div className="mt-2 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={!isDirty || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SettingsPage;
